/**
 * DeliverableLayout (ARCH-005 §3).
 *
 * The single component that knows the deliverable visual contract. Renders,
 * in order: optional Pro video walkthrough -> the per-type renderer body ->
 * the four-section meta-note (skeleton / voice decisions / what I left out /
 * do it yourself next time, fixed order, PR-17) -> the Sources line. Per-type
 * renderers render ONLY the body slot and never the meta-note or sources
 * (ARCH-005 Don't). Video shows only when video_url is present and the tier
 * is Pro (defense in depth, ARCH-005 §4 / Risks).
 *
 * Path note: ARCH-005 specifies apps/portal/src/renderers/. Phase G file
 * ownership is scoped to components/portal/**, so the renderer registry lives
 * at components/portal/renderers/. The architecture (registry + shared layout
 * + markdown fallback, no per-renderer meta-note) is preserved exactly; only
 * the directory differs. Flagged in IMPL-NOTES for the validator (mirrors the
 * existing api-contracts -> api-schemas path note already in the codebase).
 */

import type { ReactNode } from 'react';
import type { Source, VideoProvider } from '@studio/api-schemas';
import { SourcesLine } from './SourcesLine';
import { toEmbedUrl } from './video';

export interface DeliverableMetaNote {
  skeleton: string | null;
  voice_decisions: string | null;
  what_left_out: string | null;
  do_yourself_next_time: string | null;
  sources_line: string | null;
}

const META_SECTIONS: ReadonlyArray<{
  key: keyof Omit<DeliverableMetaNote, 'sources_line'>;
  label: string;
}> = [
  { key: 'skeleton', label: 'The skeleton' },
  { key: 'voice_decisions', label: 'Voice decisions' },
  { key: 'what_left_out', label: 'What I left out' },
  { key: 'do_yourself_next_time', label: 'Do it yourself next time' },
];

export function DeliverableLayout({
  body,
  metaNote,
  sources,
  videoUrl,
  videoProvider = null,
  isPro,
}: {
  body: ReactNode;
  metaNote: DeliverableMetaNote | null;
  sources: Source[];
  videoUrl: string | null;
  /** T-063 / TECH-15: Loom / YouTube / Vimeo. Drives share-URL -> embed-URL
   *  normalization. Optional so existing callers stay source-compatible;
   *  when absent the provider is inferred from the URL host. */
  videoProvider?: VideoProvider | string | null;
  isPro: boolean;
}) {
  // T-063: the Studio variant suppresses the video entirely (isPro gate,
  // defense in depth per ARCH-005 §4 / Risks - the API also refuses to write
  // video_url on a Studio-tier task). Pro variant renders the normalized
  // embed above the meta-note.
  const embedUrl = isPro ? toEmbedUrl(videoUrl, videoProvider) : null;
  const hasMetaSections =
    metaNote != null &&
    META_SECTIONS.some((s) => {
      const v = metaNote[s.key];
      return v != null && v.trim().length > 0;
    });
  return (
    <article className="space-y-8">
      {embedUrl ? (
        <figure className="overflow-hidden rounded-[10px] border border-[color:var(--color-navy-deepest)]">
          <figcaption className="bg-[color:var(--color-navy-deepest)] px-4 py-2.5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-linen)]">
              Walkthrough
            </span>
          </figcaption>
          <div className="aspect-video bg-[color:var(--color-navy-deepest)]">
            <iframe
              src={embedUrl}
              title="Walkthrough video"
              className="h-full w-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </figure>
      ) : null}

      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-white)] p-6 lg:p-8">
        {body}
      </div>

      {metaNote ? (
        <section
          aria-label="Meta-note"
          className="space-y-5 rounded-[10px] border border-[color:var(--color-line-dark)] bg-[color:var(--color-linen)] p-6 lg:p-8"
        >
          {hasMetaSections ? (
            <header>
              <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-text-mute)]">
                Meta-note
              </p>
              <h2 className="mt-1 font-display text-2xl italic text-[color:var(--color-black)]">
                Why I built it this way.
              </h2>
            </header>
          ) : null}
          {META_SECTIONS.map((s) => {
            const value = metaNote[s.key];
            if (!value || value.trim().length === 0) return null;
            return (
              <div key={s.key}>
                <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[color:var(--color-rose-dark)]">
                  {s.label}
                </h3>
                <p className="whitespace-pre-line text-sm leading-relaxed text-[color:var(--color-black)]">
                  {value}
                </p>
              </div>
            );
          })}
          <SourcesLine sources={sources} fallbackLine={metaNote.sources_line} />
        </section>
      ) : (
        <SourcesLine sources={sources} />
      )}
    </article>
  );
}
